import { Link } from '@tanstack/react-router';
import { CircleArrowLeft, Loader2 } from 'lucide-react';

import { useGetMobileInventorIntegration } from '~entities/mobile-inventor-push/api/get-integration';

import { Alert, AlertDescription, AlertTitle } from '~shared/shadcn/ui/alert';
import { Button } from '~shared/shadcn/ui/button';

import { PushAutomatedScenariosSection } from './ui/push-automated-scenarios-section';
import { PushComposeForm } from './ui/push-compose-form';
import { PushIntegrationSettings } from './ui/push-integration-settings';
import { PushSendHistory } from './ui/push-send-history';

export const PushNotificationPage = () => {
  const {
    data: integration,
    isLoading: integrationLoading,
    isError: integrationError,
    refetch: refetchIntegration,
    isFetching: integrationFetching,
  } = useGetMobileInventorIntegration();
  const integrationReady =
    integration?.configured === true &&
    integration?.mobileInventorEnabled === true &&
    integration?.appConnected === true;

  if (integrationLoading) {
    return (
      <div className='flex justify-center py-16'>
        <Loader2 className='size-10 animate-spin' />
      </div>
    );
  }

  if (integrationError) {
    return (
      <div className='mx-auto max-w-xl space-y-4'>
        <Link to='/dashboard' className='flex w-max items-center gap-2'>
          <CircleArrowLeft className='size-6' />
          <h2 className='text-2xl font-semibold'>Push Notification</h2>
        </Link>
        <Alert variant='destructive'>
          <AlertTitle>Could not load push settings</AlertTitle>
          <AlertDescription>
            Something went wrong while loading Mobile Inventor integration
            status. Please try again.
          </AlertDescription>
        </Alert>
        <Button
          variant='outline'
          disabled={integrationFetching}
          onClick={() => void refetchIntegration()}
        >
          {integrationFetching ? 'Retrying…' : 'Retry'}
        </Button>
      </div>
    );
  }

  if (!integration?.configured) {
    return (
      <div className='mx-auto max-w-xl space-y-4'>
        <Link to='/dashboard' className='flex w-max items-center gap-2'>
          <CircleArrowLeft className='size-6' />
          <h2 className='text-2xl font-semibold'>Push Notification</h2>
        </Link>
        <Alert>
          <AlertTitle>Push notifications unavailable</AlertTitle>
          <AlertDescription>
            This studio does not have a location ID configured for push
            notifications.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className='mx-auto max-w-5xl space-y-8 pb-12'>
      <div className='space-y-2'>
        <Link to='/dashboard' className='flex w-max items-center gap-2'>
          <CircleArrowLeft className='size-6' />
          <h2 className='text-2xl font-semibold'>Push Notification</h2>
        </Link>
        <p className='text-sm text-muted-foreground'>
          Manage automated notifications, compose manual broadcasts, and review
          delivery history for your studio app.
        </p>
      </div>

      <PushIntegrationSettings />

      {integration.configured && integration.appConnected !== true ? (
        <Alert variant='destructive'>
          <AlertTitle>Mobile Inventor app not connected</AlertTitle>
          <AlertDescription>
            {integration.connectionError ??
              'Mobile Inventor does not recognize this studio’s location ID.'}
            {integration.orgId ? (
              <>
                {' '}
                Location ID sent to Mobile Inventor:{' '}
                <code className='text-xs'>{integration.orgId}</code>. Contact
                Mobile Inventor to link your branded app to this location.
              </>
            ) : null}
          </AlertDescription>
        </Alert>
      ) : null}

      {!integrationReady ? (
        <Alert>
          <AlertTitle>
            {integration.mobileInventorEnabled === true
              ? 'Push compose unavailable'
              : 'Push notifications are disabled'}
          </AlertTitle>
          <AlertDescription>
            {integration.appConnected !== true
              ? integration.mobileInventorEnabled === true
                ? 'Your Mobile Inventor app is not connected. You can turn push off above, or resolve the connection to compose and send again.'
                : 'Resolve the Mobile Inventor connection above before enabling push notifications.'
              : 'Enable push notifications above to compose messages and send automated notifications.'}
          </AlertDescription>
        </Alert>
      ) : (
        <>
          <PushAutomatedScenariosSection />
          <PushComposeForm appName={integration.appName} />
        </>
      )}

      <PushSendHistory />
    </div>
  );
};
